// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {filter, find, get, set} from 'lodash';

// Core Libs
import {parseJson} from '../../lib/core/object';

// Components
import Checkbox from '../core/checkbox';
import DataViewer from '../core/data-viewer';
import Input from '../core/input';
import Loading from '../core/loading';
import RecordDetails from '../core/record-details';
import ResetButton from '../core/reset-button';
import ServicesSearchForm from '../app/services-search-form';

// Containers
import withSearch from '../../containers/with-search';

// Actions
import {setFieldValue} from '../../actions/form-data-actions';

// Styles
import './mediaitem-json.scss';

const servicesDataKey = 'mediaItemJsonRadish';

class RadishDetail extends Component {
    state = {
        echostarId: null,
    };

    componentDidMount() {
        if (get(this.getMediaItem(), 'guid')) {
            this.setFormValues();
            this.setState({
                echostarId: this.getMediaItemEchostarId(),
            });
        }
    }

    componentDidUpdate(nextProps) {
        const {fetching, formValues} = this.props;

        if (this.getMediaItemGuid() !== this.getMediaItemGuid(nextProps)) {
            this.setFormValues(nextProps);
        } else if (!fetching && nextProps.fetching) {
            this.setState({
                echostarId: get(nextProps.formValues, 'echostarId'),
            });

            if (!get(formValues, 'echostarId')) {
                this.setFormValues(nextProps);
            }
        }
    }

    getFormProps() {
        return {
            className: 'mediaitem-json--form',
            noFocus: true,
            noRefreshOnReset: true,
            noScroll: true,
            onSubmit: this.handleFormSubmit,
            servicesDataKey,
        };
    }

    getMediaItem(props = this.props) {
        return get(props.dependentSearchValues, 'content.0');
    }

    getMediaItemEchostarId(props = this.props) {
        const mediaItem = this.getMediaItem(props);

        return get(mediaItem, 'amsFranchise.echostarId');
    }

    getMediaItemGuid(props = this.props) {
        const mediaItem = this.getMediaItem(props);

        return get(mediaItem, 'guid');
    }

    getRecordData() {
        const searchData = this.getSearchData();
        const {echostarEpisodeId, json} = searchData;
        const formattedJson = parseJson(json, {});

        if (!this.isMobile() && !this.showAllEpisodes() && echostarEpisodeId) {
            set(formattedJson, 'episodes', filter(formattedJson.episodes, ['echostar_id', `${echostarEpisodeId}`]));
        }

        return formattedJson;
    }

    getRecordDetailsProps() {
        const searchData = this.getSearchData();
        const {echostarId: echostarIdState} = this.state;
        const {echostarId, echostarEpisodeId, json} = searchData;

        if (echostarEpisodeId && !this.isMobile()) {
            const formattedJson = parseJson(json, {});

            set(searchData, 'episodeNotFound', !find(formattedJson.episodes, ['echostar_id', `${echostarEpisodeId}`]));
        }

        if (!echostarId) {
            set(searchData, 'echostarId', echostarIdState);
        }

        return {
            blacklistKeys: ['json'],
            className: 'mediaitem-json--record-details',
            noDetailExpand: true,
            searchData: [
                {
                    ...searchData,
                },
            ],
            whitelistKeys: [
                'echostarId',
                'echostarContentType',
                'echostarEpisodeId',
                'radishEndpoint',
                'episodeNotFound',
            ],
        };
    }

    getSearchData() {
        const {searchData} = this.props;

        return {...get(searchData, this.isMobile() ? '1' : '0', {})};
    }

    setFormValues(props = this.props) {
        const {echostarId: echostarIdState} = this.state;
        const echostarId = this.getMediaItemEchostarId(props);

        if (echostarIdState !== echostarId) {
            props.setFieldValue(servicesDataKey, 'echostarId', echostarId);
        }
    }

    handleFormSubmit = () => {
        const {formValues, refreshServicesData, setFieldValue} = this.props;

        setFieldValue(
            'mediaItemsEnhanced',
            'amsFranchise.echostarId||amsEpisode.echostarId',
            get(formValues, 'echostarId')
        ).then(() => {
            refreshServicesData('mediaItemsEnhanced');
        });
    };

    isMobile() {
        const {dashboardProps = {}} = this.props;
        return get(dashboardProps, 'mobile', false);
    }

    showAllEpisodes() {
        const {formValues} = this.props;
        return get(formValues, '#showAllEpisodes') === 'true';
    }

    renderDataViewer() {
        const {searchData} = this.props;

        return searchData.length ? (
            <DataViewer
                className="mediaitem-json--data-viewer"
                defaultExpandKeys={['onlinenow', 'content']}
                searchData={[this.getRecordData()]}
            />
        ) : (
            <div className="mediaitem-json--no-items">Radish data not available for this Media Item.</div>
        );
    }

    renderForm() {
        return (
            <ServicesSearchForm {...this.getFormProps()}>
                <Input label="Echostar ID" name="echostarId" submitOnBlur={true} validate={['number']} />
                <Checkbox label="Show All Episodes" name="#showAllEpisodes" value="true" />
                <ResetButton className="mediaitem-json--reset-button" />
            </ServicesSearchForm>
        );
    }

    renderRecordDetails() {
        return <RecordDetails {...this.getRecordDetailsProps()} />;
    }

    render() {
        const {fetching} = this.props;

        return (
            <div className="mediaitem-json">
                {this.renderForm()}
                <div className="mediaitem-json--title">Search Information</div>
                {this.renderRecordDetails()}
                <div className="mediaitem-json--title">Radish {this.isMobile() ? 'Mobile' : 'Web'} Data</div>
                {this.renderDataViewer()}
                <Loading loading={fetching} />
            </div>
        );
    }
}

RadishDetail.propTypes = {
    dashboardProps: PropTypes.object,
    dependentSearchValues: PropTypes.object,
    error: PropTypes.string,
    fetching: PropTypes.bool,
    formValues: PropTypes.object.isRequired,
    refreshServicesData: PropTypes.func.isRequired,
    routerParams: PropTypes.object.isRequired,
    searchData: PropTypes.array.isRequired,
    setFieldValue: PropTypes.func.isRequired,
};

const mapDispatchToProps = {setFieldValue};

export default connect(null, mapDispatchToProps)(withSearch(RadishDetail, servicesDataKey));
